// Ambient traffic: cars driving the OSM road centrelines, on the left as in Delhi, picking a turn at every junction.
// Cosmetic and per client (the server knows nothing of it), and only simulated around the camera. Traffic never enters
// the protest zone or a gate, follows the car in front, and stops for people on the road. A car caught on a road
// when it closes stops short of the closed area's line and fades out. The line itself is drawn by whatever closed it
// (the stage's tape, Chalo's police lines): the kerb-to-kerb barricades that once stood there are gone (docs/plans/2026-10-19-fixes-round-1.md, A3).
import type { GameMap } from '../shared/map';
import CARS from './cars.json';
import FLEET_ATLAS from './fleet.json';
import SPRITES from './sprites.json';

/** Classes cars drive on: target speed (m/s), and how much traffic a metre of it gets (spawns and turn choice). */
export const CLASSES: Record<string, { mps: number; share: number }> = {
  primary: { mps: 14, share: 1.2 }, // the doorstep map's arterials (Jantar Mantar's spec draws none)
  primary_link: { mps: 9, share: 0.6 },
  secondary: { mps: 12, share: 1 },
  secondary_link: { mps: 9, share: 0.6 },
  tertiary: { mps: 10, share: 0.8 },
  tertiary_link: { mps: 8, share: 0.5 },
  unclassified: { mps: 7, share: 0.4 },
  residential: { mps: 6.5, share: 0.3 },
  service: { mps: 4, share: 0.06 },
};
/** Tiles cars never drive over: the protest zone, gates, and anything solid. */
const BLOCKED = new Set(['P', 'G']);

/**
 * Which sprites drive and how often: Delhi's small hatchbacks, sedans, taxis and two-wheelers most, the sports cars and
 * trucks rarely. Sprites are drawn at PPM atlas px per metre (a 48 px sedan is 4.4 m); `ppm` overrides it for a kind.
 * van2 and van3 are left out (see assets/vehicles/README.md). A row with `atlas: 'fleet'` is drawn from the CC0 fleet
 * (public/sprites/fleet.png, made by tools/make-sprites.py: autos, the bus, the surveillance van, the Kenney cars).
 */
const PPM = 11;
/**
 * The toy-scale city (docs/plans/2026-10-17-toy-scale.md): cars are drawn, and keep their gaps, CAR_SCALE over life size
 * to sit with the people (4x), and drive PACE times their class speed, accelerating and braking as hard, so they stay
 * quicker than people who now walk at 14 m/s.
 */
export const CAR_SCALE = 3.0, PACE = 2.8;
export const FLEET: { s: string; n: number; ppm?: number; k?: number; atlas?: 'fleet' }[] = [
  ...['figo', 'figo2', 'tida', 'tida2', 'tida3', 'sunny', 'mini', 'lancer', 'lancer2'].map((s) => ({ s, n: 6 })),
  ...['taxi', 'taxi2'].map((s) => ({ s, n: 7 })),
  ...['bike', 'bike2'].map((s) => ({ s, n: 9, ppm: 16, k: 1.05 })),
  ...['lexus', 'lexus2', 'lexus3', 'bmw', 'bmw2', 'bmw3', 'suv', 'suv2', 'patrol', 'patrol2', 'landcruiser', 'landcruiser2', 'landcruiser3'].map((s) => ({ s, n: 2 })),
  ...['van', 'pickup', 'pickup2', 'pickup3', 'gwagon', 'gwagon2', 'raptor', 'raptor2', 'wrangler', 'wrangler2', 'wrangler3', 'wrangler4'].map((s) => ({ s, n: 1 })),
  ...['camaro', 'camaro2', 'challenger2', 'challenger3', 'mustang2', 'mustang3', 'lambo', 'lambo2', 'porsche', 'convertible'].map((s) => ({ s, n: 0.4 })),
  ...['truck2', 'truck3', 'tow_truck', 'tow_truck2', 'tow_truck3'].map((s) => ({ s, n: 0.5, k: 0.8 })),
  { s: 'dumptruck', n: 0.3, k: 0.7 },
  // autos, Delhi's commonest three-wheeler: CNG green with a lemon-yellow top, and a few black and yellow
  { s: 'auto_rickshaw', n: 8, k: 0.85, atlas: 'fleet' },
  { s: 'auto_rickshaw_black', n: 2, k: 0.85, atlas: 'fleet' },
];
export type FleetRow = (typeof FLEET)[number];

/**
 * The all-CC0 fleet (the asset plan's phase 5): only the fleet atlas, made by tools/make-sprites.py from CC0 models,
 * for when the car pack's licence is not confirmed (assets/vehicles/README.md). Sedans, taxis and hatchbacks commonest,
 * then the autos; vans, SUVs and trucks rarely. On with CC0_FLEET, or ?fleet=cc0 in the page's address.
 */
export const CC0_FLEET_ROWS: FleetRow[] = [
  { s: 'kenney-car-kit/sedan', n: 14, atlas: 'fleet' },
  { s: 'kenney-car-kit/taxi', n: 12, atlas: 'fleet' },
  { s: 'kenney-car-kit/hatchback-sports', n: 10, atlas: 'fleet' },
  { s: 'auto_rickshaw', n: 8, k: 0.85, atlas: 'fleet' },
  { s: 'auto_rickshaw_black', n: 2, k: 0.85, atlas: 'fleet' },
  { s: 'kenney-car-kit/suv', n: 4, atlas: 'fleet' },
  { s: 'kenney-car-kit/van', n: 2, atlas: 'fleet' },
  ...['delivery', 'delivery-flat', 'truck', 'truck-flat'].map((m): FleetRow => ({ s: `kenney-car-kit/${m}`, n: 0.6, k: 0.8, atlas: 'fleet' })),
  { s: 'kenney-car-kit/garbage-truck', n: 0.3, k: 0.7, atlas: 'fleet' },
];
/** Switch the whole game to the CC0 fleet (if the car pack's licence falls through). */
export const CC0_FLEET = false;
/** The fleet that drives: the CC0 one when CC0_FLEET is set or the address asks for ?fleet=cc0. */
export function activeFleet(search = typeof location !== 'undefined' ? location.search : ''): FleetRow[] {
  return CC0_FLEET || new URLSearchParams(search).get('fleet') === 'cc0' ? CC0_FLEET_ROWS : FLEET;
}

/** One car per this many metres of lane (weighted by class share) in the simulated area, up to MAX_CARS. */
const SPACING_M = 30;
const MAX_CARS = 320;
const HEADWAY_S = 1.1, MIN_GAP_M = 1.8, LANE_HALF_M = 1.5, PERSON_HALF_M = 2.2;
const ACCEL = 2.8 * PACE, BRAKE = 9 * PACE; // m/s^2
const FADE_S = 0.5;
/** A car caught on a closing road stops with its front this far short of the area's line (metres). */
const STOP_M = 2.4;
const CELL_M = 12; // neighbour grid for following

export interface Lane {
  /** world px, already offset sideways into the lane */
  ax: number; ay: number; bx: number; by: number;
  len: number; ux: number; uy: number;
  /** lanes a car may take from the end of this one (never the way it came) */
  next: number[];
  /** px/s */
  speed: number;
  share: number;
  /** the road and direction it belongs to, so a road's lanes are told apart from its twin */
  road: number; dir: 1 | -1;
  /** px from the lane to the kerb on its left (the pavement a summoned car pulls in beside) */
  kerb: number;
}

export interface Car {
  lane: number; s: number; v: number; vmax: number; next: number;
  kind: number; len: number; wid: number;
  x: number; y: number; ux: number; uy: number;
  /** drawn position and heading, eased towards the simulated one so lane changes at junctions don't jump */
  rx: number; ry: number; rh: number;
  alpha: number; dying: boolean;
  /** fading out because its road was closed (slower than an ordinary fade) */
  closing?: boolean;
  /** distance along its lane it must not pass (short of the closed area's line), while closing */
  stop?: number;
  /** a summoned car (story mode's cab): the lanes still to drive, and where on the last one it pulls in */
  route?: number[]; stopLane?: number; stopS?: number;
}

/** A car sent to a kerb by summon(): where it stops, and whether it has. */
export interface Summoned { car: Car; at: { x: number; y: number }; kerb: { x: number; y: number }; etaS: number; arrived: boolean }

export interface Person { x: number; y: number }
type Rect = [number, number, number, number]; // x0, y0, x1, y1 in world px

export interface Network { lanes: Lane[]; metre: number; buckets: Map<number, { lanes: number[]; weight: number }>; bucketPx: number }

/** Lane graph from the map's road centrelines. Null when the map has none (the hand-made maps). */
export function buildNetwork(m: GameMap): Network | null {
  if (!m.roadLines?.length) return null;
  const t = m.tile, metre = t / (m.mpt || 1);
  /** `end`: a segment's end, where a building may touch the junction but the zone and gates still count */
  const blockedAt = (x: number, y: number, end: boolean) => {
    const tx = Math.floor(x / t), ty = Math.floor(y / t);
    if (tx < 0 || ty < 0 || tx >= m.w || ty >= m.h) return !end;
    return (!end && m.solid[ty * m.w + tx] === 1) || BLOCKED.has(m.rows[ty][tx]);
  };
  const segBlocked = (x1: number, y1: number, x2: number, y2: number) => {
    // an eighth of a tile apart, so a diagonal can't clip the corner of a gate or zone tile between two samples
    const n = Math.max(2, Math.ceil(Math.hypot(x2 - x1, y2 - y1) / (t / 8)));
    for (let i = 0; i <= n; i++) if (blockedAt(x1 + ((x2 - x1) * i) / n, y1 + ((y2 - y1) * i) / n, i === 0 || i === n)) return true;
    return false;
  };
  const key = (x: number, y: number) => `${Math.round(x * 1000)},${Math.round(y * 1000)}`; // tile units, as in the file

  const lanes: Lane[] = [];
  const startsAt = new Map<string, number[]>(), endKey: string[] = [];
  const twin = new Map<number, number>(); // lane -> the same segment the other way
  m.roadLines.forEach((r, ri) => {
    const cls = CLASSES[r.c];
    if (!cls) return;
    const p = r.l.map((v) => v * t), n = p.length / 2;
    if (n < 2) return;
    // two-way roads: drive a quarter of the width left of the centreline; one-way halves keep to their middle
    const off = r.o ? 0 : (r.w / 4) * metre;
    for (const dir of r.o ? [1] as const : [1, -1] as const) {
      const pts = dir === 1 ? p : reversePairs(p);
      const q = off ? offsetLeft(pts, off) : pts;
      const keys = [];
      for (let i = 0; i < n; i++) keys.push(key(pts[2 * i] / t, pts[2 * i + 1] / t));
      for (let i = 0; i < n - 1; i++) {
        const k = dir === 1 ? i : n - 2 - i; // the segment index along the file's order, to pair twins
        const ax = q[2 * i], ay = q[2 * i + 1], bx = q[2 * i + 2], by = q[2 * i + 3];
        const len = Math.hypot(bx - ax, by - ay);
        if (len < 1e-3 || segBlocked(pts[2 * i], pts[2 * i + 1], pts[2 * i + 2], pts[2 * i + 3]) || segBlocked(ax, ay, bx, by)) continue;
        const id = lanes.length;
        lanes.push({ ax, ay, bx, by, len, ux: (bx - ax) / len, uy: (by - ay) / len, next: [], speed: cls.mps * PACE * metre, share: cls.share, road: ri * 4096 + k, dir, kerb: (r.o ? r.w / 2 : r.w / 4) * metre });
        let list = startsAt.get(keys[i]); if (!list) startsAt.set(keys[i], (list = [])); list.push(id);
        endKey.push(keys[i + 1]);
      }
    }
  });
  const byRoad = new Map<number, number[]>();
  lanes.forEach((l, i) => { let a = byRoad.get(l.road); if (!a) byRoad.set(l.road, (a = [])); a.push(i); });
  for (const pair of byRoad.values()) if (pair.length === 2) { twin.set(pair[0], pair[1]); twin.set(pair[1], pair[0]); }
  lanes.forEach((l, i) => { l.next = (startsAt.get(endKey[i]) || []).filter((j) => j !== twin.get(i)); });

  // buckets of lanes by midpoint, so spawning only looks near the camera
  const bucketPx = 120 * metre, buckets: Network['buckets'] = new Map();
  lanes.forEach((l, i) => {
    const k = bucketKey((l.ax + l.bx) / 2, (l.ay + l.by) / 2, bucketPx);
    let b = buckets.get(k); if (!b) buckets.set(k, (b = { lanes: [], weight: 0 }));
    b.lanes.push(i); b.weight += l.len * l.share;
  });
  return { lanes, metre, buckets, bucketPx };
}

const bucketKey = (x: number, y: number, size: number) => Math.floor(y / size) * 65536 + Math.floor(x / size);

function reversePairs(p: number[]): number[] {
  const out = new Array(p.length);
  for (let i = 0, n = p.length / 2; i < n; i++) { out[2 * i] = p[2 * (n - 1 - i)]; out[2 * i + 1] = p[2 * (n - 1 - i) + 1]; }
  return out;
}

/** The polyline moved `d` to the left of its direction (y down), mitred at the bends so consecutive segments meet. */
function offsetLeft(p: number[], d: number): number[] {
  const n = p.length / 2, out = new Array(p.length);
  const normal = (i: number) => { // left normal of segment i: (uy, -ux)
    const dx = p[2 * i + 2] - p[2 * i], dy = p[2 * i + 3] - p[2 * i + 1], l = Math.hypot(dx, dy) || 1;
    return [dy / l, -dx / l];
  };
  for (let i = 0; i < n; i++) {
    const a = normal(Math.max(0, i - 1)), b = normal(Math.min(n - 2, i));
    let nx = a[0] + b[0], ny = a[1] + b[1];
    const l = Math.hypot(nx, ny);
    if (l < 1e-6) { nx = b[0]; ny = b[1]; } else { nx /= l; ny /= l; }
    const miter = Math.min(2, 1 / Math.max(0.5, nx * b[0] + ny * b[1]));
    out[2 * i] = p[2 * i] + nx * d * miter; out[2 * i + 1] = p[2 * i + 1] + ny * d * miter;
  }
  return out;
}

type AtlasRect = { x: number; y: number; w: number; h: number };
/** The two vehicle atlases: the car pack's, and the CC0 fleet's. Both front up, at PPM px per metre. */
export const ATLASES = { cars: CARS, fleet: FLEET_ATLAS } as const;
const atlasOf = (f: FleetRow) => ATLASES[f.atlas || 'cars'];
export const spriteOf = (f: FleetRow) => (atlasOf(f).sprites as Record<string, AtlasRect>)[f.s];

/** Which lanes cross a tile mask (sampled every half tile): those are closed while that area is the march's. */
export function closedLanes(net: Network, m: GameMap, mask: Uint8Array): Uint8Array {
  const out = new Uint8Array(net.lanes.length), step = m.tile / 2;
  net.lanes.forEach((l, i) => {
    for (let s = 0; s <= l.len; s += step) {
      const x = l.ax + l.ux * Math.min(s, l.len), y = l.ay + l.uy * Math.min(s, l.len);
      const tx = Math.floor(x / m.tile), ty = Math.floor(y / m.tile);
      if (tx >= 0 && ty >= 0 && tx < m.w && ty < m.h && mask[ty * m.w + tx]) { out[i] = 1; return; }
    }
  });
  return out;
}

/** Per lane, how far along it the tile mask begins (sampled every eighth of a tile); Infinity where it never does. */
export function closedAt(net: Network, m: GameMap, mask: Uint8Array): Float32Array {
  const out = new Float32Array(net.lanes.length).fill(Infinity), step = m.tile / 8;
  net.lanes.forEach((l, i) => {
    for (let s = 0; s <= l.len + step; s += step) {
      const d = Math.min(s, l.len), tx = Math.floor((l.ax + l.ux * d) / m.tile), ty = Math.floor((l.ay + l.uy * d) / m.tile);
      if (tx >= 0 && ty >= 0 && tx < m.w && ty < m.h && mask[ty * m.w + tx]) { out[i] = d; return; }
    }
  });
  return out;
}

/** The simulation: DOM free, so tests can drive it. */
export class Traffic {
  cars: Car[] = [];
  private filled = false;
  /** lanes closed to traffic (1 = closed): the march's roads; cars on them fade out over closeFadeS */
  closed: Uint8Array | null = null;
  closeFadeS = 3;
  /** with `closed`: where each closed lane meets the area, so a car caught on it stops short of the line */
  stopAt: Float32Array | null = null;
  private seenClosed: Uint8Array | null = null;
  /** off in tests that place cars by hand */
  spawning = true;
  /** the rows that drive (those with a sprite), and their total weight; a car's `kind` indexes `fleet` */
  readonly fleet: FleetRow[];
  private readonly fleetSum: number;
  constructor(readonly net: Network, private rnd: () => number = Math.random, rows: FleetRow[] = activeFleet()) {
    this.fleet = rows.filter((f) => spriteOf(f));
    this.fleetSum = this.fleet.reduce((a, f) => a + f.n, 0);
  }

  /**
   * Advance by dt seconds. `view` is the world rectangle on screen; cars live in it plus a margin, spawn out of sight
   * where they can, and are dropped once they leave the margin.
   */
  step(dt: number, view: Rect, people: Person[] = []) {
    const { lanes, metre } = this.net;
    dt = Math.min(dt, 0.1);
    const vw = view[2] - view[0], vh = view[3] - view[1];
    const pad = Math.max(80 * metre, 0.35 * Math.max(vw, vh));
    const area: Rect = [view[0] - pad, view[1] - pad, view[2] + pad, view[3] + pad];
    const inside = (r: Rect, x: number, y: number) => x >= r[0] && x <= r[2] && y >= r[1] && y <= r[3];

    this.cars = this.cars.filter((c) => (c === this.called?.car && !c.dying) || (inside(area, c.x, c.y) && !(c.dying && c.alpha <= 1e-6)));
    if (this.closed !== this.seenClosed) this.onClose();
    if (this.spawning) this.spawn(area, view);

    // neighbour grid of cars and people
    const cell = CELL_M * metre, grid = new Map<number, (Car | Person)[]>();
    const put = (o: Car | Person) => { const k = bucketKey(o.x, o.y, cell); let a = grid.get(k); if (!a) grid.set(k, (a = [])); a.push(o); };
    for (const c of this.cars) put(c);
    for (const p of people) if (inside(area, p.x, p.y)) put(p);

    for (const c of this.cars) {
      const L = lanes[c.lane];
      // slow for the bend into the next lane
      let vt = c.vmax;
      if (c.next >= 0) {
        const N = lanes[c.next], cos = L.ux * N.ux + L.uy * N.uy;
        const vTurn = c.vmax * Math.max(0.3, 0.3 + 0.7 * Math.max(0, cos));
        const left = L.len - c.s;
        vt = Math.min(vt, Math.sqrt(vTurn * vTurn + 2 * BRAKE * 0.4 * metre * left));
      } else if (c.stopLane !== c.lane) vt = Math.min(vt, Math.sqrt(2 * BRAKE * 0.5 * metre * Math.max(0, L.len - c.s - c.len / 2)));
      if (c.stop !== undefined) vt = Math.min(vt, Math.sqrt(2 * BRAKE * 0.6 * metre * Math.max(0, c.stop - c.s)));
      if (c.stopLane === c.lane) vt = Math.min(vt, Math.sqrt(2 * BRAKE * 0.4 * metre * Math.max(0, c.stopS! - c.s)));
      // whatever is ahead in the lane: a car going our way, or a person anywhere on it
      const look = (c.v * c.v) / (2 * BRAKE * metre) + 14 * CAR_SCALE * metre;
      let gap = Infinity;
      const gx0 = Math.floor((c.x - look) / cell), gx1 = Math.floor((c.x + look) / cell);
      const gy0 = Math.floor((c.y - look) / cell), gy1 = Math.floor((c.y + look) / cell);
      for (let gy = gy0; gy <= gy1; gy++) for (let gx = gx0; gx <= gx1; gx++) {
        for (const o of grid.get(gy * 65536 + gx) || []) {
          if (o === c) continue;
          // cars on our lane, on the lane we turn into, or turning into it too are measured along the lanes: of two
          // cars heading for the same lane, the one nearer the join goes first, so a merge never stacks them up
          if ('lane' in o) {
            const left = L.len - c.s;
            const lane = o.lane === c.lane ? o.s - c.s : o.lane === c.next ? left + o.s : c.next >= 0 && o.next === c.next ? left - (lanes[o.lane].len - o.s) : NaN;
            if (!Number.isNaN(lane)) {
              if (lane > 0 && lane <= look) gap = Math.min(gap, lane - (c.len + o.len) / 2);
              continue;
            }
          }
          const dx = o.x - c.x, dy = o.y - c.y, along = dx * c.ux + dy * c.uy;
          if (along <= 0 || along > look) continue;
          const side = Math.abs(dx * c.uy - dy * c.ux);
          if ('lane' in o) {
            if (side > LANE_HALF_M * metre || o.ux * c.ux + o.uy * c.uy < 0.2) continue; // oncoming or crossing: not ours
            gap = Math.min(gap, along - (c.len + o.len) / 2);
          } else {
            if (side > PERSON_HALF_M * metre) continue;
            gap = Math.min(gap, along - c.len / 2 - 0.5 * metre);
          }
        }
      }
      if (gap < Infinity) vt = Math.min(vt, Math.max(0, (gap - MIN_GAP_M * CAR_SCALE * metre) / HEADWAY_S));
      c.v += Math.max(-BRAKE * metre * dt, Math.min(ACCEL * metre * dt, vt - c.v));
      if (gap < 0.3 * metre) c.v = 0;
      c.s += c.v * dt;
      if (c.stop !== undefined && c.s > c.stop) { c.s = c.stop; c.v = 0; }
      if (c.stopLane === c.lane && c.s >= c.stopS! - 0.3 * metre) { c.s = Math.min(c.s, c.stopS!); c.v = 0; if (this.called?.car === c) this.called.arrived = true; }
      while (c.s > lanes[c.lane].len) {
        if (c.next < 0) { c.s = lanes[c.lane].len; c.v = 0; c.dying = true; break; }
        c.s -= lanes[c.lane].len; c.lane = c.next; c.next = c.route ? c.route.shift() ?? -1 : this.pickNext(c.lane);
      }
      if (c.v < 0.05 * metre && c.next < 0 && c.stopLane === undefined && lanes[c.lane].len - c.s < c.len) c.dying = true; // parked at a dead end
      this.place(c);
      c.alpha = c.dying ? Math.max(0, c.alpha - dt / (c.closing ? this.closeFadeS : FADE_S)) : Math.min(1, c.alpha + dt / FADE_S);
      const k = Math.min(1, dt * 8);
      c.rx += (c.x - c.rx) * k; c.ry += (c.y - c.ry) * k;
      const h = Math.atan2(c.uy, c.ux);
      let dh = h - c.rh; dh -= Math.round(dh / (2 * Math.PI)) * 2 * Math.PI;
      c.rh += dh * Math.min(1, dt * 7);
    }
  }

  /**
   * Roads just closed (or opened). A car on a closed lane fades out over closeFadeS, braking to a stop short of the
   * area's line if it has not reached it yet, and where it is if it has. A car whose next turn closed takes another.
   */
  private onClose() {
    this.seenClosed = this.closed;
    const C = this.closed, metre = this.net.metre;
    if (!C) return;
    for (const c of this.cars) {
      if (C[c.lane]) {
        c.dying = c.closing = true;
        const line = this.stopAt ? this.stopAt[c.lane] : Infinity;
        c.stop = Math.max(c.s, Math.min(line, this.net.lanes[c.lane].len) - STOP_M * metre - c.len / 2);
      } else if (c.next >= 0 && C[c.next]) c.next = this.pickNext(c.lane);
    }
  }

  private place(c: Car) {
    const L = this.net.lanes[c.lane];
    c.x = L.ax + L.ux * c.s; c.y = L.ay + L.uy * c.s; c.ux = L.ux; c.uy = L.uy;
  }

  /** The summoned car, if any (one at a time). */
  called: Summoned | null = null;

  /**
   * Send a car with sprite `sprite` to the kerb nearest `to` (world px): it starts about `fromM` metres of lane away,
   * out of sight where it can, drives there on the shortest lane route and pulls in. `at`: it is already parked there
   * (an arrival's drop-off). Returns null when no lane is near. Call leave() to send it on its way.
   */
  summon(to: { x: number; y: number }, sprite: string, fromM = 260, at = false): Summoned | null {
    const { lanes, metre } = this.net;
    let T = -1, sT = 0, best = Infinity;
    lanes.forEach((L, i) => {
      if (this.closed?.[i]) return;
      const s = Math.max(0, Math.min(L.len, (to.x - L.ax) * L.ux + (to.y - L.ay) * L.uy));
      const d = Math.hypot(L.ax + L.ux * s - to.x, L.ay + L.uy * s - to.y);
      if (d < best) { best = d; T = i; sT = s; }
    });
    if (T < 0 || best > 40 * metre) return null;
    // shortest lane distance to T's stop from the start of every lane, walking the graph backwards
    const prev: number[][] = lanes.map(() => []);
    lanes.forEach((L, i) => { for (const j of L.next) prev[j].push(i); });
    const dist = new Float64Array(lanes.length).fill(Infinity), via = new Int32Array(lanes.length).fill(-1);
    dist[T] = sT;
    const open = [T];
    while (open.length) {
      let bi = 0; for (let k = 1; k < open.length; k++) if (dist[open[k]] < dist[open[bi]]) bi = k;
      const j = open.splice(bi, 1)[0];
      for (const i of prev[j]) {
        if (this.closed?.[i]) continue;
        const d = dist[j] + lanes[i].len;
        if (d < dist[i]) { if (dist[i] === Infinity) open.push(i); dist[i] = d; via[i] = j; }
      }
    }
    // the start: the lane whose route is nearest fromM long (a little more is better than less)
    let S = T, bs = Infinity;
    if (!at) dist.forEach((d, i) => { if (d < Infinity) { const k = Math.abs(d / metre - fromM) * (d / metre < fromM ? 1.5 : 1); if (k < bs) { bs = k; S = i; } } });
    const route: number[] = [];
    for (let i = via[S]; i >= 0 && route.length < 500; i = via[i]) route.push(i);
    const kind = this.fleet.findIndex((f) => f.s === sprite);
    const c = this.make(S, at ? sT : 0, !at, Math.max(0, kind));
    c.route = route; c.next = route.shift() ?? -1; c.stopLane = T; c.stopS = sT; c.v = at ? 0 : c.v;
    if (at) c.alpha = 1;
    this.cars.push(c);
    const L = lanes[T], speed = route.reduce((a, i) => a + lanes[i].speed, lanes[S].speed) / (route.length + 1);
    const ax = L.ax + L.ux * sT, ay = L.ay + L.uy * sT, k = L.kerb + 1.5 * metre; // the pavement, on the car's left (y down)
    this.called = { car: c, at: { x: ax, y: ay }, kerb: { x: ax + L.uy * k, y: ay - L.ux * k }, etaS: at ? 0 : (dist[S] - (at ? sT : 0)) / (speed * 0.7), arrived: at };
    return this.called;
  }

  /** The summoned car drives off along the traffic, and is an ordinary car again. */
  leave() {
    const c = this.called?.car;
    this.called = null;
    if (!c) return;
    c.stopLane = c.stopS = c.route = undefined;
    if (c.next < 0) c.next = this.pickNext(c.lane);
  }

  /** A turn at the end of the lane: busier roads and going straight on are likelier. -1 at a dead end. */
  pickNext(lane: number): number {
    const L = this.net.lanes[lane], opts = this.closed ? L.next.filter((j) => !this.closed![j]) : L.next;
    if (!opts.length) return -1;
    if (opts.length === 1) return opts[0];
    const w = opts.map((j) => { const N = this.net.lanes[j]; return N.share * (0.25 + Math.max(0, L.ux * N.ux + L.uy * N.uy)); });
    let r = this.rnd() * w.reduce((a, b) => a + b, 0);
    for (let i = 0; i < opts.length; i++) if ((r -= w[i]) <= 0) return opts[i];
    return opts[opts.length - 1];
  }

  /** Top up to the density for `area`: out of sight when it can, anywhere on the first fill. */
  private spawn(area: Rect, view: Rect) {
    const { lanes, metre, buckets, bucketPx } = this.net;
    const near: { lanes: number[]; weight: number }[] = [];
    let total = 0;
    for (let by = Math.floor(area[1] / bucketPx); by <= Math.floor(area[3] / bucketPx); by++)
      for (let bx = Math.floor(area[0] / bucketPx); bx <= Math.floor(area[2] / bucketPx); bx++) {
        const b = buckets.get(by * 65536 + bx);
        if (b) { near.push(b); total += b.weight; }
      }
    if (!total) return;
    const target = Math.min(MAX_CARS, Math.round(total / (SPACING_M * metre)));
    const anywhere = !this.filled || this.cars.length < target * 0.5;
    for (let tries = 0; this.cars.length < target && tries < (anywhere ? target * 4 : 30); tries++) {
      let r = this.rnd() * total, b = near[0];
      for (const x of near) if ((r -= x.weight) <= 0) { b = x; break; }
      let r2 = this.rnd() * b.weight, lane = b.lanes[0];
      for (const j of b.lanes) if ((r2 -= lanes[j].len * lanes[j].share) <= 0) { lane = j; break; }
      if (this.closed?.[lane]) continue;
      const L = lanes[lane], s = this.rnd() * L.len, x = L.ax + L.ux * s, y = L.ay + L.uy * s;
      if (x < area[0] || x > area[2] || y < area[1] || y > area[3]) continue;
      const seen = x > view[0] - 6 * metre && x < view[2] + 6 * metre && y > view[1] - 6 * metre && y < view[3] + 6 * metre;
      if (seen && !anywhere) continue;
      if (this.cars.some((c) => Math.abs(c.x - x) < 9 * CAR_SCALE * metre && Math.abs(c.y - y) < 9 * CAR_SCALE * metre)) continue;
      this.cars.push(this.make(lane, s, seen));
    }
    this.filled = true;
  }

  private make(lane: number, s: number, seen: boolean, fixed = -1): Car {
    const metre = this.net.metre, L = this.net.lanes[lane];
    let r = this.rnd() * this.fleetSum, kind = 0;
    for (let i = 0; i < this.fleet.length; i++) if ((r -= this.fleet[i].n) <= 0) { kind = i; break; }
    if (fixed >= 0) kind = fixed;
    const f = this.fleet[kind], a = spriteOf(f), ppm = f.ppm || PPM;
    const vmax = L.speed * (f.k || 1) * (0.85 + 0.25 * this.rnd());
    const c: Car = {
      lane, s, v: vmax * 0.6, vmax, next: this.pickNext(lane), kind, len: (a.h / ppm) * metre * CAR_SCALE, wid: (a.w / ppm) * metre * CAR_SCALE,
      x: 0, y: 0, ux: 1, uy: 0, rx: 0, ry: 0, rh: 0, alpha: seen ? 0 : 1, dying: false,
    };
    this.place(c);
    c.rx = c.x; c.ry = c.y; c.rh = Math.atan2(c.uy, c.ux);
    return c;
  }
}

/** Traffic and its drawing: sprites from public/sprites/cars.png, a soft shadow from the dusk sun, and headlights. */
export class TrafficLayer {
  readonly sim: Traffic;
  /** each atlas the fleet draws from, once loaded */
  private imgs = new Map<string, HTMLImageElement>();
  private need: Set<string>;
  private beam: HTMLCanvasElement | null = null;
  private readonly sx: number; private readonly sy: number;

  static from(m: GameMap): TrafficLayer | null { const n = buildNetwork(m); return n && n.lanes.length ? new TrafficLayer(m, n) : null; }

  private constructor(private readonly map: GameMap, net: Network) {
    this.sim = new Traffic(net);
    this.need = new Set(this.sim.fleet.map((f) => atlasOf(f).image));
    const sun = SPRITES.light ?? [-0.458, 0.865, 0.204], h = 0.8 * net.metre; // shadow of a point 0.8 m up
    this.sx = (-sun[0] / sun[1]) * h; this.sy = (-sun[2] / sun[1]) * h;
    for (const src of this.need) {
      const img = new Image();
      img.onload = () => { this.imgs.set(src, img); };
      img.src = src;
    }
  }

  private closedFor = new WeakMap<Uint8Array, { lanes: Uint8Array; at: Float32Array }>();

  /** Close the roads inside a tile mask (null opens them all); cars on them fade out over fadeS seconds. */
  close(m: GameMap, mask: Uint8Array | null, fadeS: number) {
    this.sim.closeFadeS = fadeS;
    if (!mask) { this.sim.closed = this.sim.stopAt = null; return; }
    let c = this.closedFor.get(mask);
    if (!c) {
      c = { lanes: closedLanes(this.sim.net, m, mask), at: closedAt(this.sim.net, m, mask) };
      this.closedFor.set(mask, c);
    }
    this.sim.closed = c.lanes; this.sim.stopAt = c.at;
  }

  update(dt: number, vx0: number, vy0: number, vw: number, vh: number, people: Person[]) {
    this.sim.step(dt, [vx0, vy0, vx0 + vw, vy0 + vh], people);
  }

  /** Draw in world coordinates (the caller has applied the camera); z is the zoom, for the headlights' detail cut. */
  draw(g: CanvasRenderingContext2D, vx0: number, vy0: number, vw: number, vh: number, z: number) {
    if (this.imgs.size < this.need.size) return;
    const metre = this.sim.net.metre, pad = 12 * metre;
    const vis = this.sim.cars.filter((c) => c.rx > vx0 - pad && c.rx < vx0 + vw + pad && c.ry > vy0 - pad && c.ry < vy0 + vh + pad);
    vis.sort((a, b) => a.ry - b.ry);
    g.save();
    for (const c of vis) {
      const f = this.sim.fleet[c.kind], a = spriteOf(f), img = this.imgs.get(atlasOf(f).image)!, k = (metre / (f.ppm || PPM)) * CAR_SCALE;
      // sprites face up the screen, so heading 0 (east) is a quarter turn clockwise
      const rot = c.rh + Math.PI / 2;
      g.save(); // the shadow, offset in world space
      g.translate(c.rx + this.sx, c.ry + this.sy); g.rotate(rot);
      g.globalAlpha = 0.3 * c.alpha; g.fillStyle = '#080A10';
      roundRect(g, -c.wid * 0.45, -c.len * 0.47, c.wid * 0.9, c.len * 0.94, c.wid * 0.25); g.fill();
      g.restore();
      g.save();
      g.translate(c.rx, c.ry); g.rotate(rot);
      g.globalAlpha = c.alpha;
      g.drawImage(img, a.x, a.y, a.w, a.h, (-a.w / 2) * k, (-a.h / 2) * k, a.w * k, a.h * k);
      g.restore();
    }
    if (z * metre >= 3) { // headlights: a warm wash ahead of each car, added light
      const beam = this.beamImage();
      g.globalCompositeOperation = 'lighter';
      for (const c of vis) {
        g.globalAlpha = 0.13 * c.alpha;
        g.save(); g.translate(c.rx, c.ry); g.rotate(c.rh);
        g.drawImage(beam, c.len * 0.45, -2.6 * CAR_SCALE * metre, 8 * CAR_SCALE * metre, 5.2 * CAR_SCALE * metre);
        g.restore();
      }
    }
    g.restore();
  }

  /** A cone of sodium-white light, pointing +x, made once. */
  private beamImage(): HTMLCanvasElement {
    if (this.beam) return this.beam;
    const c = (this.beam = document.createElement('canvas')); c.width = 112; c.height = 64;
    const g = c.getContext('2d')!;
    const grad = g.createRadialGradient(0, 32, 2, 0, 32, 112);
    grad.addColorStop(0, 'rgba(255,236,190,1)'); grad.addColorStop(1, 'rgba(255,236,190,0)');
    g.fillStyle = grad;
    g.beginPath(); g.moveTo(0, 26); g.lineTo(112, 0); g.lineTo(112, 64); g.lineTo(0, 38); g.closePath(); g.fill();
    return c;
  }
}

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  g.beginPath(); g.moveTo(x + r, y); g.arcTo(x + w, y, x + w, y + h, r); g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r); g.arcTo(x, y, x + w, y, r); g.closePath();
}
